import { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";
import { AuthContext } from "../../shared/context/auth-context";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import ReactSlider from "react-slider";
import "./PerformanceComparison.css";

ChartJS.register(...registerables, zoomPlugin, annotationPlugin);

// ─── Config ────────────────────────────────────────────────────────────────
// Empty string => use relative URLs so requests flow through the nginx proxy
// (which maps /api/* to the backend). Override via REACT_APP_BACKEND_URL in .env
// only if the React dev server runs standalone (not behind nginx).
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const RANGE_PRESETS = [
  { label: "1W", days: 7 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
  { label: "3Y", days: 1095 },
  { label: "5Y", days: 1825 },
  { label: "MAX", days: null },
];

// ─── Fetch helper ───────────────────────────────────────────────────────────
// These endpoints are public (front page, no auth); with a subscriber's or an
// admin's token, the trades come with their entry dates and prices. Thin
// wrapper around fetch that rejects non-2xx responses so the caller's .catch()
// can fall through to mock data.
//
// With onProgress, a streamed series (/sp500 and /mtt send their number of
// days in X-Total-Count, then the days as they come from the database) is
// counted while it arrives: onProgress(loaded, total) after every chunk.
async function fetchJson(path, token, onProgress) {
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = "Bearer " + token;
  const res = await fetch(`${API_BASE}${path}`, { method: "GET", headers });
  if (!res.ok) throw new Error(`${path}: HTTP ${res.status}`);
  const total = Number(res.headers.get("X-Total-Count"));
  if (!onProgress || !total || !res.body) return res.json();

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let text = "", loaded = 0, from = 0;
  onProgress(0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    text += decoder.decode(value, { stream: true });
    // every point starts with {"date":; a chunk can end inside one
    for (let i = text.indexOf(POINT, from); i !== -1; i = text.indexOf(POINT, from)) {
      loaded += 1;
      from = i + POINT.length;
    }
    from = Math.max(from, text.length - POINT.length + 1);
    onProgress(loaded, total);
  }
  return JSON.parse(text + decoder.decode());
}
const POINT = '{"date":';

// "Loading data… 42 %" and how far each part is, while the page loads
function LoadingCounter({ progress }) {
  const series = [["MTT fund", progress.mtt], ["S&P 500", progress.sp500]];
  const loaded = series.reduce((sum, [, p]) => sum + (p?.loaded ?? 0), 0);
  const total = series.reduce((sum, [, p]) => sum + (p?.total ?? 0), 0);
  const known = series.every(([, p]) => p);
  const percent = known && total ? Math.floor((loaded / total) * 100) : null;
  const days = (p) => p
    ? `${p.loaded.toLocaleString("en-US")} of ${p.total.toLocaleString("en-US")} days`
    : "waiting";
  return (
    <div
      className="pc-loading-text"
      role="progressbar"
      aria-label="Loading data"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent ?? undefined}
    >
      <div>Loading data…{percent !== null && ` ${percent} %`}</div>
      <div className="pc-loading-count">
        {series.map(([label, p]) => `${label}: ${days(p)}`).join(" · ")}
        {` · Trades: ${progress.trades ? "loaded" : "loading"}`}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function normalizeToPercent(data) {
  if (!data || data.length === 0) return [];
  const base = data[0].close;
  return data.map((d) => ({
    ...d,
    performance: parseFloat((((d.close - base) / base) * 100).toFixed(4)),
  }));
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// The currency of a trade's prices: USD, converted at each day's rate by
// control; the quote currency where it has no rate (then "GBp" really is
// pence, so it stays GBp)
function currencyCode(currency) {
  return currency || "USD";
}

function formatAmount(value) {
  if (value === null || value === undefined) return "—";
  return value.toFixed(2);
}

// Without a subscription the backend leaves out the entry; these stand-ins keep
// the blurred cells looking like a date and a price.
const HIDDEN_DATE = "Jan 1, 2000";
const HIDDEN_PRICE = "000.00";

// ─── Client-side mock fallback ──────────────────────────────────────────────
// Only used if the backend is unreachable (network error, auth failure, etc.).
// The server now generates its own mocks via performance-controller.js, so a
// successful response is labeled "LIVE API" even during early development.
function generateMockData(
  days,
  startPrice = 3200,
  volatility = 0.018,
  drift = -0.48,
) {
  const data = [];
  let price = startPrice;
  const end = new Date();
  for (let i = days; i >= 0; i--) {
    const date = new Date(end);
    date.setDate(date.getDate() - i);
    const weekday = date.getDay();
    if (weekday === 0 || weekday === 6) continue;
    price = price * (1 + (Math.random() + drift) * volatility);
    data.push({
      date: date.toISOString().split("T")[0],
      close: parseFloat(price.toFixed(2)),
    });
  }
  return data;
}

const MTT_COLOR = "#00a8ff";
const TICKERS = [
  "AAPL",
  "MSFT",
  "GOOGL",
  "AMZN",
  "TSLA",
  "NVDA",
  "META",
  "JPM",
  "V",
  "WMT",
];

function tradeSort(a, b) {
  const aActive = !a.exitDate;
  const bActive = !b.exitDate;
  if (aActive !== bActive) return aActive ? -1 : 1;
  return new Date(b.exitDate) - new Date(a.exitDate);
}

function generateMockTrades(count = 40) {
  const trades = [];
  const end = new Date();
  const activeCount = 4;
  for (let i = 0; i < count; i++) {
    const isActive = i < activeCount;
    const daysAgo = isActive
      ? Math.floor(Math.random() * 30)
      : Math.floor(10 + Math.random() * 365 * 5);
    const exitDate = new Date(end);
    exitDate.setDate(exitDate.getDate() - daysAgo);
    const ticker = TICKERS[Math.floor(Math.random() * TICKERS.length)];
    const buyPrice = parseFloat((50 + Math.random() * 450).toFixed(2));
    const positionSize = Math.floor(1 + Math.random() * 50) * 100;
    const perfPct = parseFloat(((Math.random() - 0.4) * 60).toFixed(2));
    trades.push({
      exitDate: isActive ? null : exitDate.toISOString().split("T")[0],
      entryDate: exitDate.toISOString().split("T")[0],
      ticker,
      buyPrice,
      exitPrice: parseFloat((buyPrice * (1 + perfPct / 100)).toFixed(2)),
      positionSize,
      performance: perfPct,
      status: isActive ? "active" : "closed",
    });
  }
  return trades.sort(tradeSort);
}

// The front page and the logged-in home page. Everyone sees all trades in the
// selected range; only admins and subscribers see where they were entered.
const PerformanceComparison = () => {
  const { role, isPaid, token } = useContext(AuthContext);
  const showEntries = role === "admin" || isPaid;
  const chartRef = useRef(null);
  const [allData, setAllData] = useState([]);
  const [allMttData, setAllMttData] = useState([]);
  const [visibleData, setVisibleData] = useState([]);
  const [visibleMttData, setVisibleMttData] = useState([]);
  const [activePreset, setActivePreset] = useState("1Y");
  const [sliderRange, setSliderRange] = useState([0, 0]);
  const [loading, setLoading] = useState(true);
  // { sp500, mtt: { loaded, total } once their size is known, trades: true once loaded }
  const [progress, setProgress] = useState({});
  const [allTrades, setAllTrades] = useState([]);
  // "total_return": the S&P 500 with dividends reinvested, like the fund's
  // prices (backend: SP500_TR); "price" before that series has data
  const [sp500Index, setSp500Index] = useState(null);
  const [dataSource, setDataSource] = useState({
    sp500: null,
    mtt: null,
    trades: null,
  }); // "api" | "mock"
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width: 600px)").matches,
  );

  // Track viewport so the chart can thin out axis ticks / legend on phones.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 600px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Fetch SP500, MTT and trades in parallel.
  // Each request falls back to client-side mock data on any failure
  // (network, 401 from checkAuth, 5xx from the controller, etc.).
  useEffect(() => {
    setLoading(true);
    setProgress({});
    const counter = (key) => (loaded, total) =>
      setProgress((p) => ({ ...p, [key]: { loaded, total } }));
    // done, streamed or not (mock data)
    const complete = (key) => (result) => {
      const n = result.data.length;
      setProgress((p) => ({ ...p, [key]: { loaded: n, total: n } }));
      return result;
    };

    const fetchSp500 = fetchJson("/api/performance/sp500", undefined, counter("sp500"))
      .then((body) => {
        // Backend returns { source: "live" | "backend-mock", index, data: [...] }
        const raw = body?.data ?? [];
        return {
          data: [...raw].sort((a, b) => new Date(a.date) - new Date(b.date)),
          source: body?.source === "live" ? "live" : "backend-mock",
          index: body?.index ?? null,
        };
      })
      .catch((err) => {
        console.warn("SP500 fetch failed, using frontend mock:", err.message);
        return {
          data: generateMockData(365 * 5, 3200, 0.018, -0.48),
          source: "frontend-mock",
        };
      })
      .then(complete("sp500"));

    const fetchMtt = fetchJson("/api/performance/mtt", undefined, counter("mtt"))
      .then((body) => {
        const raw = body?.data ?? [];
        return {
          data: [...raw].sort((a, b) => new Date(a.date) - new Date(b.date)),
          source: body?.source === "live" ? "live" : "backend-mock",
        };
      })
      .catch((err) => {
        console.warn("MTT fetch failed, using frontend mock:", err.message);
        return {
          data: generateMockData(365 * 5, 150, 0.025, -0.46),
          source: "frontend-mock",
        };
      })
      .then(complete("mtt"));

    const fetchTrades = fetchJson("/api/performance/trades", token)
      .then((body) => {
        const raw = body?.data ?? [];
        return {
          data: [...raw].sort(tradeSort),
          source: body?.source === "live" ? "live" : "backend-mock",
        };
      })
      .catch((err) => {
        console.warn("Trades fetch failed, using frontend mock:", err.message);
        return {
          data: generateMockTrades(40),
          source: "frontend-mock",
        };
      })
      .then((result) => {
        setProgress((p) => ({ ...p, trades: true }));
        return result;
      });

    Promise.all([fetchSp500, fetchMtt, fetchTrades]).then(
      ([sp500, mtt, trades]) => {
        setAllData(sp500.data);
        setSp500Index(sp500.index);
        setAllMttData(mtt.data);
        setAllTrades(trades.data);
        setDataSource({
          sp500: sp500.source,
          mtt: mtt.source,
          trades: trades.source,
        });
        setLoading(false);
      },
    );
  }, [token]);

  // Apply preset — computes the index range, then sets sliderRange
  const applyPreset = useCallback(
    (preset) => {
      setActivePreset(preset.label);
      if (!allData.length) return;

      const endIdx = allData.length - 1;

      if (preset.label === "MAX") {
        // Use the later of the two series' first dates so both are fully visible
        const sp500Start = allData[0]?.date;
        const mttStart   = allMttData[0]?.date;
        const laterStart = sp500Start && mttStart
          ? (sp500Start >= mttStart ? sp500Start : mttStart)
          : sp500Start;
        let startIdx = laterStart
          ? allData.findIndex((d) => d.date >= laterStart)
          : 0;
        if (startIdx < 0) startIdx = 0;
        setSliderRange([startIdx, endIdx]);
        return;
      }

      const endDate = new Date(allData[endIdx].date);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - preset.days);
      let startIdx = allData.findIndex((d) => new Date(d.date) >= startDate);
      if (startIdx < 0) startIdx = 0;
      setSliderRange([startIdx, endIdx]);
    },
    [allData, allMttData],
  );

  // Sync visible data with slider range
  useEffect(() => {
    if (!allData.length) return;
    const slice = allData.slice(sliderRange[0], sliderRange[1] + 1);
    setVisibleData(normalizeToPercent(slice));
    // Build MTT slice aligned to the same date range
    if (allMttData.length) {
      const startDate = allData[sliderRange[0]]?.date;
      const endDate = allData[sliderRange[1]]?.date;
      const mttSlice = allMttData.filter(
        (d) => d.date >= startDate && d.date <= endDate,
      );
      setVisibleMttData(normalizeToPercent(mttSlice));
    }
  }, [allData, allMttData, sliderRange]);

  // Initialize to 1Y on load
  useEffect(() => {
    if (allData.length && allMttData.length)
      applyPreset(RANGE_PRESETS.find((p) => p.label === "1Y"));
  }, [allData.length, allMttData.length]); // eslint-disable-line

  const perf = visibleData.length
    ? (visibleData[visibleData.length - 1]?.performance ?? 0)
    : 0;
  const mttPerf = visibleMttData.length
    ? (visibleMttData[visibleMttData.length - 1]?.performance ?? 0)
    : 0;
  const isPositive = perf >= 0;
  const isMttPositive = mttPerf >= 0;
  const accentColor = isPositive ? "#00d97e" : "#ff4757";

  const startDateLabel = visibleData[0]
    ? formatDate(visibleData[0].date)
    : "--";
  const endDateLabel = visibleData.length
    ? formatDate(visibleData[visibleData.length - 1].date)
    : "--";

  // Filter trades to selected date range
  const visibleTrades = useMemo(() => {
    if (!allTrades.length || !allData.length) return [];
    const rangeStart = allData[sliderRange[0]]?.date;
    const rangeEnd = allData[sliderRange[1]]?.date;
    if (!rangeStart || !rangeEnd) return [];
    return allTrades.filter((t) =>
      t.exitDate === null || t.exitDate === undefined
        ? true
        : t.exitDate >= rangeStart && t.exitDate <= rangeEnd,
    );
  }, [allTrades, allData, sliderRange]);

  // ─── Chart.js config ────────────────────────────────────────────────────────
  const mttPerfMap = useMemo(() => {
    const map = {};
    visibleMttData.forEach((d) => {
      map[d.date] = d.performance;
    });
    return map;
  }, [visibleMttData]);

  const chartData = useMemo(() => {
    if (!visibleData.length) return { labels: [], datasets: [] };
    const labels = visibleData.map((d) => d.date);
    return {
      labels,
      datasets: [
        {
          label: sp500Index === "total_return" ? "S&P 500 TR" : "S&P 500",
          data: visibleData.map((d) => d.performance),
          fill: true,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const { ctx: c, chartArea } = chart;
            if (!chartArea) return "transparent";
            const gradient = c.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom,
            );
            if (isPositive) {
              gradient.addColorStop(0, "rgba(0, 217, 126, 0.25)");
              gradient.addColorStop(1, "rgba(0, 217, 126, 0)");
            } else {
              gradient.addColorStop(0, "rgba(255, 71, 87, 0.25)");
              gradient.addColorStop(1, "rgba(255, 71, 87, 0)");
            }
            return gradient;
          },
          borderColor: accentColor,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: accentColor,
          pointHoverBorderColor: "#0d0d0f",
          pointHoverBorderWidth: 2,
          tension: 0.1,
        },
        {
          label: "MTT",
          data: labels.map((date) => mttPerfMap[date] ?? null),
          fill: false,
          borderColor: MTT_COLOR,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: MTT_COLOR,
          pointHoverBorderColor: "#0d0d0f",
          pointHoverBorderWidth: 2,
          tension: 0.1,
          spanGaps: true,
        },
      ],
    };
  }, [visibleData, accentColor, isPositive, mttPerfMap, sp500Index]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      scales: {
        x: {
          type: "category",
          grid: {
            color: "#14141a",
            drawBorder: false,
          },
          ticks: {
            color: "#3a3a4a",
            font: { family: "'IBM Plex Mono', monospace", size: isMobile ? 9 : 10 },
            maxTicksLimit: isMobile ? 4 : 8,
            maxRotation: 0,
            autoSkip: true,
            callback: function (val) {
              const label = this.getLabelForValue(val);
              const d = new Date(label);
              return d.toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
              });
            },
          },
          border: { display: false },
        },
        y: {
          grid: {
            color: "#14141a",
            drawBorder: false,
          },
          ticks: {
            color: "#3a3a4a",
            font: { family: "'IBM Plex Mono', monospace", size: isMobile ? 9 : 10 },
            maxTicksLimit: isMobile ? 6 : undefined,
            callback: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`,
          },
          border: { display: false },
        },
      },
      plugins: {
        legend: {
          display: true,
          labels: {
            color: "#888",
            font: { family: "'IBM Plex Mono', monospace", size: isMobile ? 10 : 11 },
            usePointStyle: true,
            pointStyle: "line",
            padding: isMobile ? 12 : 20,
            boxWidth: isMobile ? 24 : 40,
          },
        },
        tooltip: {
          backgroundColor: "#0d0d0f",
          borderColor: "#2a2a35",
          borderWidth: 1,
          titleColor: "#666",
          titleFont: { family: "'IBM Plex Mono', monospace", size: 11 },
          bodyFont: {
            family: "'IBM Plex Mono', monospace",
            size: 14,
            weight: "bold",
          },
          padding: { top: 10, bottom: 10, left: 16, right: 16 },
          cornerRadius: 8,
          displayColors: true,
          boxWidth: 8,
          boxHeight: 8,
          boxPadding: 6,
          callbacks: {
            title: (items) => {
              if (!items.length) return "";
              return formatDate(items[0].label);
            },
            label: (item) => {
              const v = item.parsed.y;
              if (v === null || v === undefined) return null;
              return ` ${item.dataset.label}: ${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
            },
          },
        },
        annotation: {
          annotations: {
            zeroLine: {
              type: "line",
              yMin: 0,
              yMax: 0,
              borderColor: "#2a2a35",
              borderWidth: 1,
            },
          },
        },
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
          },
          zoom: {
            wheel: { enabled: false },
            pinch: { enabled: false },
            mode: "x",
          },
        },
      },
    }),
    [accentColor, isMobile],
  );

  return (
    <div className="pc-wrapper">
      <div className="pc-card" style={{ "--accent": accentColor }}>

        {/* Header */}
        <div className="pc-header-row">
          <div>
            <div className="pc-title-row">
              <div className="pc-live-dot" />
              <span className="pc-section-label">Performance Comparison</span>
            </div>
            <div className="pc-perf-row">
              <div className="pc-perf-group">
                <span className="pc-ticker-name">{sp500Index === "total_return" ? "SPX TR" : "SPX"}</span>
                <span className="pc-perf-value pc-perf-value--spx">
                  {isPositive ? "+" : ""}{perf.toFixed(2)}%
                </span>
              </div>
              <div className="pc-divider" />
              <div className="pc-perf-group">
                <span className="pc-ticker-name">MTT</span>
                <span className={`pc-perf-value ${isMttPositive ? "pc-perf-value--mtt-pos" : "pc-perf-value--mtt-neg"}`}>
                  {isMttPositive ? "+" : ""}{mttPerf.toFixed(2)}%
                </span>
              </div>
            </div>
            <div className="pc-date-range">{startDateLabel} — {endDateLabel}</div>
          </div>

          <div className="pc-preset-row">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.label}
                className={`preset-btn${activePreset === p.label ? " active" : ""}`}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="pc-chart-wrapper">
          {loading && <LoadingCounter progress={progress} />}
          {!loading && visibleData.length > 0 && (
            <Line ref={chartRef} data={chartData} options={chartOptions} />
          )}
        </div>

        {/* Range Slider */}
        {allData.length > 1 && (
          <div className="pc-slider-section">
            <div className="pc-slider-meta">
              <span>DRAG TO ADJUST RANGE</span>
              <span>{visibleData.length} data points</span>
            </div>
            <ReactSlider
              className="perf-slider"
              thumbClassName="perf-slider-thumb"
              trackClassName="perf-slider-track"
              min={0}
              max={allData.length - 1}
              value={sliderRange}
              onChange={(val) => { setSliderRange(val); setActivePreset(null); }}
              pearling
              minDistance={1}
            />
            <div className="pc-slider-end-labels">
              <span>{allData[0] ? formatDate(allData[0].date) : ""}</span>
              <span>{allData.length ? formatDate(allData[allData.length - 1].date) : ""}</span>
            </div>
          </div>
        )}

        {/* Zoom hint */}
        <div className="pc-hint-row">
          <span>
            SCROLL TO ZOOM · DRAG TO PAN
            {sp500Index === "total_return" && " · S&P 500 WITH DIVIDENDS REINVESTED, LIKE THE FUND"}
          </span>
        </div>

        {/* Trades Table */}
        {!loading && (
          <div className={`pc-trades-section${showEntries ? " pc-trades-section--unblurred" : ""}`}>
            <div className="pc-trades-header-row">
              <span className="pc-trades-label">Trades</span>
              <span className="pc-trades-count">{visibleTrades.length} trades in range</span>
            </div>
            <div className="pc-table-wrapper">
              <div className="pc-table-head">
                <span />
                <span style={{ textAlign: "left" }}>Ticker</span>
                <span className="pc-th-entry-date" style={{ textAlign: "right" }}>Entry Date</span>
                <span style={{ textAlign: "right" }}>Exit Date</span>
                <span className="pc-table-head-right pc-th-entry-price">Entry Price</span>
                <span className="pc-table-head-right">Exit Price</span>
                <span className="pc-table-head-right pc-th-position">Position</span>
                <span className="pc-table-head-right">Perf.</span>
              </div>
              <div className="trades-scroll pc-table-scroll">
                {visibleTrades.length === 0 && (
                  <div className="pc-table-empty">No trades in selected range</div>
                )}
                {visibleTrades.map((trade, i) => {
                  const isActive = trade.status === "active";
                  return (
                    <div key={i} className={`pc-table-row${isActive ? " pc-table-row--active" : ""}`}>
                      <span className="pc-dot-cell">
                        {isActive && <span className="pc-active-dot" />}
                      </span>
                      <span className="pc-ticker-cell">{trade.ticker}</span>
                      <span className="pc-entry-date-cell">
                        {!showEntries ? HIDDEN_DATE : trade.entryDate ? formatDate(trade.entryDate) : "—"}
                      </span>
                      <span className={`pc-exit-date-cell${isActive ? " pc-exit-date-cell--active" : ""}`}>
                        {isActive ? "OPEN" : formatDate(trade.exitDate)}
                      </span>
                      <span className="pc-entry-price-cell">
                        <span className="pc-ccy">{currencyCode(trade.currency)}</span>
                        <span className="pc-amount">{showEntries ? formatAmount(trade.buyPrice) : HIDDEN_PRICE}</span>
                      </span>
                      <span className={`pc-price-cell${isActive ? " pc-price-cell--active" : ""}`}>
                        <span className="pc-ccy">{currencyCode(trade.currency)}</span>
                        <span className="pc-amount">{formatAmount(trade.exitPrice)}</span>
                      </span>
                      <span className="pc-position-cell">{(trade.positionSize ?? 0).toLocaleString()}</span>
                      <span className={`pc-perf-cell ${(trade.performance ?? 0) >= 0 ? "pc-perf-cell--pos" : "pc-perf-cell--neg"}`}>
                        {(trade.performance ?? 0) >= 0 ? "+" : ""}{(trade.performance ?? 0).toFixed(2)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Data source indicators.
            source values: "live" | "backend-mock" | "frontend-mock" | null */}
        {!loading && (dataSource.sp500 || dataSource.mtt || dataSource.trades) && (
          <div className="pc-source-row">
            {[
              { label: "SPX", source: dataSource.sp500 },
              { label: "MTT", source: dataSource.mtt },
              { label: "TRADES", source: dataSource.trades },
            ].map(({ label, source }) => {
              // Map each source to its display text and modifier class.
              // The className keeps the existing --mock / --live variants working
              // and adds a new --backend-mock variant for the middle state.
              let text;
              let variant;
              if (source === "live") {
                text = "LIVE API";
                variant = "pc-source-chip--live";
              } else if (source === "backend-mock") {
                text = "BACKEND MOCK";
                variant = "pc-source-chip--backend-mock";
              } else {
                text = "FRONTEND MOCK";
                variant = "pc-source-chip--mock";
              }
              return (
                <div key={label} className={`pc-source-chip ${variant}`}>
                  {label}: {text}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="pc-footer">
          <span className="pc-footer-text pc-footer-text--left">
            PERFORMANCE INDEXED TO RANGE START = 0%
          </span>
          <span className="pc-footer-text">
            {(() => {
              // Footer summarizes where the data came from across all three
              // endpoints. "Live" wins if any source is live; otherwise pick
              // the more specific label between backend-mock and frontend-mock.
              const sources = [dataSource.sp500, dataSource.mtt, dataSource.trades];
              if (sources.includes("live")) return "DATA: MONGODB VIA NODE";
              if (sources.includes("backend-mock")) return "DATA: BACKEND-GENERATED MOCK";
              return "DATA: FRONTEND-GENERATED MOCK";
            })()}
          </span>
        </div>

      </div>
    </div>
  );
};

export default PerformanceComparison;